import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CHANGE_LOG_ENTRY_PAGE_SIZE,
  CHANGE_LOG_PAGE_SIZE,
  fetchChangeLogEntriesPage,
  fetchChangeLogPage,
} from "../utils/changeLogApi";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import { showToast } from "../utils/toastOptions";

/** Stable unique key for a change log. Prefer id when present so logs with same timestamp/performer don't overwrite. */
export function changeLogKey(log) {
  if (log?.id != null) return String(log.id);
  const batchKey =
    Array.isArray(log?.batch_ids) && log.batch_ids.length > 0 ? log.batch_ids[0] : "n";
  return `${log?.timestamp ?? ""}-${log?.performed_by ?? ""}-${batchKey}`;
}

/** Update a log already shown from pagination; ignore SSE for logs on unloaded pages. */
export function mergeChangeLogIntoMap(prev, log) {
  const k = changeLogKey(log);
  if (!prev.has(k)) return prev;
  const next = new Map(prev);
  const { entries: _entries, ...meta } = log;
  const existing = prev.get(k) || {};
  next.set(k, {
    ...existing,
    ...meta,
    entry_count:
      log.entry_count ?? (Array.isArray(log.entries) ? log.entries.length : existing.entry_count),
  });
  return next;
}

function createEntriesState(pageSize = CHANGE_LOG_ENTRY_PAGE_SIZE) {
  return {
    entries: [],
    page: 1,
    totalPages: 1,
    loading: false,
    entryCount: null,
    pageSize,
  };
}

const MIN_PAGE_SIZE = PAGE_SIZES[0];

const EXPAND_COL_WIDTH = 40;
const DATE_CELL_PL = "4px";

const HEADER_CELL_SX = {
  py: 1.25,
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "text.secondary",
  bgcolor: "grey.50",
  borderBottom: "1px solid",
  borderColor: "grey.200",
  whiteSpace: "nowrap",
};

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

function TablePaginationBar({
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  disabled,
  labelId,
  size = "medium",
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 2,
        pt: 1,
        pl: 1,
        pr: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" component="span" sx={{ color: "text.primary" }}>
          Rows per page
        </Typography>
        <Select
          labelId={labelId}
          size="small"
          variant="standard"
          disableUnderline
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          disabled={disabled}
          sx={{
            fontSize: "0.875rem",
            "& .MuiSelect-select": { py: 0.5, pr: 3 },
          }}
        >
          {PAGE_SIZES.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {totalPages > 1 && (
        <Pagination
          size={size}
          count={totalPages}
          page={page}
          onChange={(_, newPage) => onPageChange(newPage)}
          showFirstButton
          showLastButton
          disabled={disabled}
        />
      )}
    </Box>
  );
}

function totalPagesFromCount(totalCount, pageSize) {
  if (!Number.isFinite(totalCount) || totalCount <= 0) return 1;
  return Math.max(1, Math.ceil(totalCount / pageSize));
}

export default function ChangeLogsTab({
  companyId,
  projectName = null,
  refreshKey = 0,
  logUpdate = null,
  focusLogId = null,
  onFocusHandled,
}) {
  const [highlightKey, setHighlightKey] = useState(null);
  const rowRefs = useRef(new Map());
  const [changeLogs, setChangeLogs] = useState([]);
  const [entriesByLogKey, setEntriesByLogKey] = useState(() => new Map());
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const paginationKey = companyId ? `changelog:${companyId}:${projectName || "all"}` : null;
  const [pagination, setPagination] = usePersistedPagination(paginationKey, {
    pageIndex: 0,
    pageSize: CHANGE_LOG_PAGE_SIZE,
  });
  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;
  const setCurrentPage = (pageOrUpdater) => {
    setPagination((prev) => {
      const nextPage =
        typeof pageOrUpdater === "function" ? pageOrUpdater(prev.pageIndex + 1) : pageOrUpdater;
      return { ...prev, pageIndex: Math.max(0, nextPage - 1) };
    });
  };
  const setPageSize = (sizeOrUpdater) => {
    setPagination((prev) => ({
      pageIndex: 0,
      pageSize: typeof sizeOrUpdater === "function" ? sizeOrUpdater(prev.pageSize) : sizeOrUpdater,
    }));
  };
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const listOptions = useMemo(() => (projectName ? { projectName } : {}), [projectName]);

  const loadPage = useCallback(
    async (page, { forceRefresh = false } = {}) => {
      if (!companyId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchChangeLogPage(companyId, page, {
          pageSize,
          forceRefresh,
          ...listOptions,
        });
        const logs = data?.logs || [];
        setChangeLogs(logs);
        const count = Number.isFinite(data?.total_count) ? data.total_count : logs.length;
        setTotalCount(count);
        setTotalPages(totalPagesFromCount(count, pageSize));
        // Page change replaces the visible list, so reset expanded + entries state.
        setEntriesByLogKey(new Map());
        setExpandedKeys(new Set());
      } catch (err) {
        const message = err?.message || "Failed to fetch change logs";
        setError(message);
        showToast("error", message);
      } finally {
        setLoading(false);
      }
    },
    [companyId, listOptions, pageSize]
  );

  const loadEntriesPage = useCallback(
    async (logKey, changeLogId, page, pageSizeArg) => {
      if (!companyId || !changeLogId) return;
      const effectivePageSize = pageSizeArg ?? CHANGE_LOG_ENTRY_PAGE_SIZE;
      setEntriesByLogKey((prev) => {
        const next = new Map(prev);
        const current = next.get(logKey) || createEntriesState(effectivePageSize);
        next.set(logKey, { ...current, loading: true, pageSize: effectivePageSize });
        return next;
      });
      try {
        const data = await fetchChangeLogEntriesPage(companyId, changeLogId, page, {
          pageSize: effectivePageSize,
          ...listOptions,
        });
        const newEntries = data?.entries || [];
        const entryTotalPages = totalPagesFromCount(data?.total_count, effectivePageSize);
        setEntriesByLogKey((prev) => {
          const next = new Map(prev);
          next.set(logKey, {
            entries: newEntries,
            page,
            totalPages: entryTotalPages,
            loading: false,
            entryCount: data?.total_count ?? null,
            pageSize: effectivePageSize,
          });
          return next;
        });
      } catch (err) {
        const message = err?.message || "Failed to fetch change log entries";
        showToast("error", message);
        setEntriesByLogKey((prev) => {
          const next = new Map(prev);
          const current = next.get(logKey) || createEntriesState(effectivePageSize);
          next.set(logKey, { ...current, loading: false });
          return next;
        });
      }
    },
    [companyId, listOptions]
  );

  const handleAccordionChange = (logKey, log) => (event, isExpanded) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(logKey);
      else next.delete(logKey);
      return next;
    });
    if (isExpanded && log?.id != null) {
      const existing = entriesByLogKey.get(logKey);
      if (!existing?.entries?.length && !existing?.loading) {
        loadEntriesPage(logKey, log.id, 1);
      }
    }
  };

  // Single effect that owns loading: resets to page 1 on filter/pageSize change,
  // then loads whenever the resolved page changes. Tracks prior filter values via
  // a ref so we can detect filter changes without double-fetching.
  const filtersRef = useRef(undefined);
  if (filtersRef.current === undefined) {
    filtersRef.current = { companyId, projectName, refreshKey, pageSize };
  }

  useEffect(() => {
    if (!companyId) {
      setChangeLogs([]);
      setEntriesByLogKey(new Map());
      setExpandedKeys(new Set());
      setTotalPages(1);
      setTotalCount(0);
      setCurrentPage(1);
      filtersRef.current = { companyId, projectName, refreshKey, pageSize };
      return;
    }
    const prev = filtersRef.current;
    const filtersChanged =
      prev.companyId !== companyId ||
      prev.projectName !== projectName ||
      prev.refreshKey !== refreshKey ||
      prev.pageSize !== pageSize;
    if (filtersChanged && currentPage !== 1) {
      // Defer load until the page reset lands; ref stays stale on purpose so the
      // next effect run still sees filtersChanged and applies forceRefresh.
      setCurrentPage(1);
      return;
    }
    const forceRefresh = filtersChanged && refreshKey > 0;
    filtersRef.current = { companyId, projectName, refreshKey, pageSize };
    loadPage(currentPage, { forceRefresh });
  }, [companyId, projectName, refreshKey, pageSize, currentPage, loadPage]);

  // SSE: only merge updates for logs visible on the current page.
  useEffect(() => {
    const log = logUpdate?.log ?? logUpdate;
    if (!log) return;
    const k = changeLogKey(log);
    setChangeLogs((prev) => {
      const idx = prev.findIndex((entry) => changeLogKey(entry) === k);
      if (idx === -1) return prev;
      const next = prev.slice();
      const { entries: _entries, ...meta } = log;
      next[idx] = {
        ...next[idx],
        ...meta,
        entry_count:
          log.entry_count ??
          (Array.isArray(log.entries) ? log.entries.length : next[idx].entry_count),
      };
      return next;
    });

    if (Array.isArray(log.entries) && log.entries.length > 0) {
      setEntriesByLogKey((prev) => {
        const next = new Map(prev);
        const current = next.get(k) || createEntriesState();
        const ps = current.pageSize ?? CHANGE_LOG_ENTRY_PAGE_SIZE;
        next.set(k, {
          ...current,
          entries: log.entries,
          page: 1,
          totalPages: totalPagesFromCount(log.entry_count ?? log.entries.length, ps),
          loading: false,
          entryCount: log.entry_count ?? log.entries.length,
          pageSize: ps,
        });
        return next;
      });
    }
  }, [logUpdate]);

  // Newly created logs are on page 1.
  useEffect(() => {
    if (focusLogId != null && currentPage !== 1) setCurrentPage(1);
  }, [focusLogId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focusLogId == null) return;
    const log = changeLogs.find((l) => Number(l.id) === Number(focusLogId));
    if (!log) return;
    const k = changeLogKey(log);
    if (!expandedKeys.has(k)) handleAccordionChange(k, log)(null, true);
    setHighlightKey(k);
    onFocusHandled?.();
    // Not tied to effect cleanup: clearing focusLogId re-runs this effect.
    requestAnimationFrame(() =>
      rowRefs.current.get(k)?.scrollIntoView({ behavior: "smooth", block: "center" })
    );
    setTimeout(() => setHighlightKey((cur) => (cur === k ? null : cur)), 2500);
  }, [focusLogId, changeLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!companyId) {
    return null;
  }

  if (loading && changeLogs.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!loading && changeLogs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {error ? error : "No change logs yet."}
      </Typography>
    );
  }

  return (
    <Box sx={{ pr: 0.5 }}>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1.5,
          bgcolor: "background.paper",
          boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
          overflowX: "auto",
        }}
      >
        <Table size="small" sx={{ minWidth: 560, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ "& th": HEADER_CELL_SX }}>
              <TableCell sx={{ width: EXPAND_COL_WIDTH, px: 0 }} />
              <TableCell sx={{ width: 200, pl: DATE_CELL_PL }}>Date &amp; Time</TableCell>
              <TableCell sx={{ width: 130 }}>Actions</TableCell>
              <TableCell>Performed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changeLogs.map((log) => {
              const logKey = changeLogKey(log);
              const entryState = entriesByLogKey.get(logKey) || createEntriesState();
              const entries = entryState.entries || [];
              const actionCount = log.entry_count ?? entryState.entryCount ?? entries.length;
              const isExpanded = expandedKeys.has(logKey);
              const toggle = () => handleAccordionChange(logKey, log)(null, !isExpanded);

              return (
                <React.Fragment key={logKey}>
                  <TableRow
                    hover
                    onClick={toggle}
                    ref={(el) => {
                      if (el) rowRefs.current.set(logKey, el);
                      else rowRefs.current.delete(logKey);
                    }}
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 600ms",
                      ...(highlightKey === logKey && {
                        bgcolor: "rgba(25,118,210,0.12) !important",
                      }),
                      "& > td": {
                        borderBottom: isExpanded ? "none" : "1px solid",
                        borderColor: "grey.200",
                        py: 0.5,
                      },
                      ...(isExpanded && { bgcolor: "action.hover" }),
                    }}
                  >
                    <TableCell sx={{ pl: 1, pr: 0 }}>
                      <IconButton
                        size="small"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle();
                        }}
                        sx={{
                          p: 0.25,
                          transition: "transform 150ms",
                          transform: isExpanded ? "rotate(180deg)" : "none",
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ pl: DATE_CELL_PL }}>
                      <Typography variant="body2">
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${actionCount} action${actionCount !== 1 ? "s" : ""}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: 12,
                            bgcolor: "primary.main",
                          }}
                        >
                          {initials(log.performed_by)}
                        </Avatar>
                        <Typography variant="body2" noWrap>
                          {log.performed_by}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        p: 0,
                        borderBottom: isExpanded ? "1px solid" : "none",
                        borderColor: "grey.200",
                      }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            bgcolor: "action.hover",
                            px: `${EXPAND_COL_WIDTH + 4}px`,
                            pb: 1.5,
                            pt: 0,
                          }}
                        >
                          {entryState.loading && entries.length === 0 ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : (
                            <>
                              <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                  overflowX: "auto",
                                  border: "1px solid",
                                  borderColor: "grey.200",
                                  borderRadius: 1.5,
                                }}
                              >
                                <Table size="small" sx={{ tableLayout: "fixed", minWidth: 720 }}>
                                  <TableHead>
                                    <TableRow sx={{ "& th": HEADER_CELL_SX }}>
                                      <TableCell sx={{ width: "6%" }}>User ID</TableCell>
                                      <TableCell sx={{ width: "10%" }}>Users</TableCell>
                                      <TableCell sx={{ width: "17%" }}>Action</TableCell>
                                      <TableCell sx={{ width: "17%" }}>Project</TableCell>
                                      <TableCell sx={{ width: "10%" }}>Status</TableCell>
                                      <TableCell sx={{ width: "40%" }}>Message</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {entries.map((entry, idx) => (
                                      <TableRow
                                        key={`${logKey}-${entry.user_id}-${entry.project_name ?? ""}-${idx}`}
                                        sx={{ "& > .MuiTableCell-root": { py: 1.25 } }}
                                      >
                                        <TableCell sx={{ width: "7%" }}>{entry.user_id}</TableCell>
                                        <TableCell sx={{ width: "11%" }}>
                                          {entry.user_name}
                                        </TableCell>
                                        <TableCell sx={{ width: "16%", wordBreak: "break-word" }}>
                                          <Typography
                                            variant="body2"
                                            component="span"
                                            sx={{
                                              color: [
                                                "Add to Project",
                                                "Create User",
                                                "Reactivate User",
                                                "Whitelist User",
                                                "Create Distribution Group",
                                                "Update Distribution Group",
                                              ].includes(entry.action)
                                                ? "success.main"
                                                : "error.main",
                                            }}
                                          >
                                            {entry.action}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "14%", wordBreak: "break-word" }}>
                                          {entry.project_name}
                                        </TableCell>
                                        <TableCell sx={{ width: "9%" }}>
                                          {entry.status_code === 200 && (
                                            <Chip
                                              label="Complete"
                                              size="small"
                                              color="success"
                                              icon={<CheckCircleIcon fontSize="small" />}
                                            />
                                          )}
                                          {entry.status_code === 202 && (
                                            <Chip
                                              label="Processing"
                                              size="small"
                                              color="warning"
                                              icon={<CircularProgress size={12} />}
                                            />
                                          )}
                                          {entry.status_code === 500 && (
                                            <Chip
                                              label="Failed"
                                              size="small"
                                              color="error"
                                              icon={<ErrorIcon fontSize="small" />}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ width: "43%", wordBreak: "break-word" }}>
                                          {entry.message}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              {(entryState.totalPages > 1 ||
                                (entryState.entryCount ?? entries.length) > MIN_PAGE_SIZE) && (
                                <TablePaginationBar
                                  labelId={`entry-page-size-label-${logKey}`}
                                  page={entryState.page}
                                  pageSize={entryState.pageSize ?? CHANGE_LOG_ENTRY_PAGE_SIZE}
                                  totalPages={entryState.totalPages}
                                  size="small"
                                  disabled={entryState.loading}
                                  onPageSizeChange={(nextSize) =>
                                    loadEntriesPage(logKey, log.id, 1, nextSize)
                                  }
                                  onPageChange={(newPage) => {
                                    if (newPage !== entryState.page) {
                                      loadEntriesPage(logKey, log.id, newPage, entryState.pageSize);
                                    }
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(totalPages > 1 || totalCount > MIN_PAGE_SIZE) && (
        <TablePaginationBar
          labelId="log-page-size-label"
          page={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          disabled={loading}
          onPageSizeChange={setPageSize}
          onPageChange={setCurrentPage}
        />
      )}
    </Box>
  );
}

ChangeLogsTab.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectName: PropTypes.string,
  refreshKey: PropTypes.number,
  logUpdate: PropTypes.object,
  focusLogId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onFocusHandled: PropTypes.func,
};
